import validate from 'validate.js'
import { Form, Field } from 'react-final-form'
import { scroller } from 'react-scroll'

import { Button, Icon, InputField } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import {
  getCheckboxState,
  NOT_VISIBLE_CHECKED,
  VISIBLE_CHECKED,
} from '@mc/gdpr'
import { PageSource } from '@mc/track-event'
import { trackConnectClick } from './events'

import { useEmailCapture } from './HeroEmailCapture/utils'

interface HeroEmailCaptureFields {
  clientEmail: string
  gdprOptIn: boolean
}

export const EmailCTA = () => {
  const { t } = useTranslation('@mc/next')
  const { loading, hasError, clearError, onSubmitEmail } = useEmailCapture()

  const CTAText = t('forBusiness.landing.header.ctaText')

  const emailCapture = getCheckboxState()

  const goToContactForm = () =>
    scroller.scrollTo('contact-form', {
      duration: 700,
      smooth: true,
      offset: -70,
    })

  const validateForm = (values: HeroEmailCaptureFields) =>
    validate(values, CONSTRAINTS, { fullMessages: false })

  const CONSTRAINTS = {
    clientEmail: {
      email: {
        message: t('forBusiness.landing.emailCapture.constraints.emailInvalid'),
      },
    },
    gdprOptIn: {
      inclusion: {
        within: [true],
        message: t('forBusiness.landing.emailCapture.constraints.gdprOptIn'),
      },
    },
  }

  return (
    <div className='mc-w-100 mc-m-auto mc-mb-6' style={{ maxWidth: '500px' }}>
      <Form
        initialValues={{
          gdprOptIn: [NOT_VISIBLE_CHECKED, VISIBLE_CHECKED].includes(
            emailCapture,
          ),
        }}
        onSubmit={({ clientEmail, gdprOptIn }: HeroEmailCaptureFields) => {
          if (clientEmail) {
            onSubmitEmail(clientEmail, gdprOptIn)
          } else {
            goToContactForm()
          }
        }}
        validate={validateForm}
        render={({ handleSubmit }) => (
          <form
            onSubmit={handleSubmit}
            onChange={clearError}
            className='d-flex'
            style={{ gap: 'var(--mc-scale-4)' }}
          >
            <Field
              component={InputField}
              name='clientEmail'
              id='clientEmail'
              type='email'
              placeholder={t('forBusiness.emailPlaceholder')}
              className='flex-1 mc-font-caption'
              autoComplete='email'
            />

            {!loading && hasError && (
              <div className='d-flex align-items-center mc-mt-2'>
                <Icon kind='error' className='mc-icon--3 mc-text--error' />
                <p
                  aria-live='assertive'
                  className='mc-text--error mc-text-x-small'
                >
                  {t('forBusiness.landing.header.emailCaptureError')}
                </p>
              </div>
            )}

            <Button
              className='mc-corners--sm'
              type='submit'
              loading={loading}
              onClick={() =>
                trackConnectClick({
                  location: 'homepage-connect-with-us',
                  pageSource: PageSource.MAW_HOME,
                })
              }
            >
              {CTAText}
            </Button>
          </form>
        )}
      />
    </div>
  )
}
